// src/utils/accessPreferenceStore.js
// Manage per-user Cust/Whs override selections in localStorage + broadcast change events.

export const ACCESS_OVERRIDE_EVENT = "pp-access-override-change";
const STORAGE_KEY = "pp_access_overrides";

const getWindow = () => (typeof window === "undefined" ? null : window);

const resolveUserKey = (userOrKey) => {
  if (!userOrKey) return null;
  if (typeof userOrKey === "string") {
    const trimmed = userOrKey.trim();
    return trimmed ? trimmed.toLowerCase() : null;
  }
  const candidates = [
    userOrKey.email,
    userOrKey.username,
    userOrKey.user_name,
    userOrKey.userId,
    userOrKey.user_id,
    userOrKey.id,
  ];
  for (const candidate of candidates) {
    if (candidate) return String(candidate).toLowerCase();
  }
  return null;
};

const safeParseJSON = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const readStore = () => {
  const win = getWindow();
  if (!win) return {};
  const parsed = safeParseJSON(win.localStorage.getItem(STORAGE_KEY));
  return parsed && typeof parsed === "object" ? parsed : {};
};

const writeStore = (map) => {
  const win = getWindow();
  if (!win) return;
  const payload = map && Object.keys(map).length > 0 ? JSON.stringify(map) : null;
  if (payload) win.localStorage.setItem(STORAGE_KEY, payload);
  else win.localStorage.removeItem(STORAGE_KEY);
};

const dispatchChange = () => {
  const win = getWindow();
  if (!win || typeof win.dispatchEvent !== "function") return;
  win.dispatchEvent(new CustomEvent(ACCESS_OVERRIDE_EVENT, { detail: Date.now() }));
};

const normalizeTokens = (tokens) => {
  if (!Array.isArray(tokens)) return null;
  const seen = new Set();
  const cleaned = [];
  tokens.forEach((token) => {
    const upper = String(token ?? "")
      .trim()
      .toUpperCase();
    if (!upper) return;
    if (seen.has(upper)) return;
    seen.add(upper);
    cleaned.push(upper);
  });
  return cleaned;
};

const updateEntry = (user, updater) => {
  if (typeof updater !== "function") return;
  const key = resolveUserKey(user);
  if (!key) return;
  const store = readStore();
  const current = store[key] && typeof store[key] === "object" ? { ...store[key] } : {};
  const next = updater(current);
  if (!next || Object.keys(next).length === 0) {
    if (store[key]) {
      delete store[key];
      writeStore(store);
      dispatchChange();
    }
    return;
  }
  store[key] = next;
  writeStore(store);
  dispatchChange();
};

export const getStoredAccessPrefs = (user) => {
  const key = resolveUserKey(user);
  if (!key) return null;
  const store = readStore();
  const entry = store[key];
  if (!entry || typeof entry !== "object") return null;
  return {
    cust: Array.isArray(entry.cust) ? [...entry.cust] : entry.cust ?? null,
    whs: Array.isArray(entry.whs) ? [...entry.whs] : entry.whs ?? null,
  };
};

export const setAccessOverrideForType = (user, type, tokens) => {
  if (!user || (type !== "cust" && type !== "whs")) return;
  if (tokens === null) {
    updateEntry(user, (current) => {
      if (current[type] === undefined) return current;
      const next = { ...current };
      delete next[type];
      return Object.keys(next).length ? next : null;
    });
    return;
  }
  const normalized = normalizeTokens(tokens);
  updateEntry(user, (current = {}) => {
    const next = { ...current };
    next[type] = normalized || [];
    return next;
  });
};

export const clearAccessOverridesForUser = (user) => {
  updateEntry(user, () => null);
};

export const clearAllAccessOverrides = () => {
  const win = getWindow();
  if (!win) return;
  win.localStorage.removeItem(STORAGE_KEY);
  dispatchChange();
};
